import React, { useState, useEffect, useMemo } from 'react';
import { DetailedItemData, ReportFilters, ItemMode } from '../../types/Report';
import { reportService } from '../../services/reportService';
import { appendReportFiltersToSearchParams } from '../../utils/reportFilters';
import ExportButton from './ExportButton';

interface DetailedItemsTableProps {
  type: 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand';
  id: string | number;
  segmentType: 'products' | 'customers' | 'salesAgents' | 'brands' | 'territories' | 'categories';
  filters: ReportFilters;
  mode: ItemMode;
  title: string;
  correlationId?: string; // Optional correlation ID for grouping related requests
}

const ITEMS_PER_PAGE = 25;

const DetailedItemsTable: React.FC<DetailedItemsTableProps> = ({
  type,
  id,
  segmentType,
  filters,
  mode,
  title,
  correlationId,
}) => {
  const [data, setData] = useState<DetailedItemData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [sortAscending, setSortAscending] = useState(false); // false = biggest losses first (default)

  useEffect(() => {
    loadData();
  }, [type, id, segmentType, filters, mode, correlationId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use provided correlation ID or generate one for this table load
      const tableCorrelationId =
        correlationId ||
        `detailed-items-${type}-${id}-${segmentType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const result = await reportService.getDetailedItemData(
        type,
        id,
        segmentType,
        filters,
        mode,
        tableCorrelationId
      );
      setData(result);
      setCurrentPage(1);
    } catch (err: any) {
      setError(err.message || 'Failed to load detailed items data');
      console.error('Error loading detailed items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await reportService.exportDetailedItemsToExcel(
        type,
        id,
        segmentType,
        filters,
        mode
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateRange =
        filters.startDate && filters.endDate
          ? `${filters.startDate}_to_${filters.endDate}`
          : new Date().toISOString().split('T')[0];
      a.download = `detailed-items-${type}-${id}-${segmentType}-${dateRange}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export Excel file');
      console.error('Error exporting Excel:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  };

  const formatPercentage = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // Calculate projection for an item based on compare mode
  const calculateProjection = (
    item: DetailedItemData
  ): { current: number | null; prior: number | null } => {
    if (!filters.startDate || !filters.endDate || !filters.compareMode) {
      return { current: null, prior: null };
    }

    const currentStart = new Date(filters.startDate);
    const currentEnd = new Date(filters.endDate);
    const currentDays =
      Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Calculate prior period dates using the same logic as buildDateRangeFilter
    let priorStart: Date | null = null;
    let priorEnd: Date | null = null;
    let priorDays: number = currentDays; // Default to same length

    if (filters.compareMode === 'yoy') {
      // Same period previous year - compare exact same date range from previous year
      priorStart = new Date(currentStart);
      priorStart.setFullYear(priorStart.getFullYear() - 1);
      priorEnd = new Date(currentEnd);
      priorEnd.setFullYear(priorEnd.getFullYear() - 1);
      priorDays =
        Math.ceil((priorEnd.getTime() - priorStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    } else if (filters.compareMode === 'previous') {
      // Previous period of same length
      const daysDiff = Math.floor(
        (currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24)
      );
      priorEnd = new Date(currentStart);
      priorEnd.setDate(priorEnd.getDate() - 1);
      priorStart = new Date(priorEnd);
      priorStart.setDate(priorStart.getDate() - daysDiff);
      priorDays =
        Math.ceil((priorEnd.getTime() - priorStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }

    const daysInYear = 365;

    let currentProjection: number | null = null;
    let priorProjection: number | null = null;

    if (currentDays > 0) {
      // Current projection: extrapolate current revenue to full year
      if (item.currentRevenue > 0) {
        currentProjection = item.currentRevenue * (daysInYear / currentDays);
      }
    }

    if (priorDays > 0) {
      // Prior projection: extrapolate prior revenue to full year using actual prior period length
      if (item.priorRevenue > 0) {
        priorProjection = item.priorRevenue * (daysInYear / priorDays);
      }
    }

    return { current: currentProjection, prior: priorProjection };
  };

  // Sort data by revenue change (biggest losses first by default)
  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      if (sortAscending) {
        return a.revenueChange - b.revenueChange; // Smallest to largest
      } else {
        return b.revenueChange - a.revenueChange; // Largest to smallest (biggest losses first)
      }
    });
    return sorted;
  }, [data, sortAscending]);

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleItemClick = (item: DetailedItemData, segmentType: string) => {
    // Map segmentType to drillDownType
    const drillDownTypeMap: { [key: string]: string } = {
      products: 'product',
      customers: 'customer',
      salesAgents: 'salesagent',
      brands: 'brand',
      territories: 'territory',
      categories: 'category',
    };

    const drillDownType = drillDownTypeMap[segmentType];
    if (!drillDownType) return;

    // For products, use productCode if available, otherwise itemId
    // Products use product_code (string) in the URL, not product ID (number)
    let itemId: string | number | undefined;
    if (drillDownType === 'product') {
      itemId = item.productCode || item.itemId;
      // Trim product codes to remove trailing spaces
      if (typeof itemId === 'string') {
        itemId = itemId.trim();
      }
    } else {
      itemId = item.itemId;
    }

    if (!itemId) return;

    const params = appendReportFiltersToSearchParams(filters);
    const url = `/reports/executive-dashboard/${drillDownType}/${itemId}?${params.toString()}`;
    window.open(url, '_blank');
  };

  if (loading && data.length === 0) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Loading...</div>
      </div>
    );
  }

  if (error && data.length === 0) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        </div>
        <div style={{ color: '#dc3545', padding: '1rem' }}>Error: {error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        </div>
        <div
          style={{ textAlign: 'center', padding: '2rem', color: '#6c757d', fontStyle: 'italic' }}
        >
          No data available
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={() => setSortAscending(!sortAscending)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f8f9fa',
              color: '#6c757d',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
            title={sortAscending ? 'Sort: Smallest to Largest' : 'Sort: Biggest Losses First'}
          >
            {sortAscending ? '↑ Smallest First' : '↓ Biggest Losses First'}
          </button>
          <ExportButton
            onClick={handleExport}
            loading={exporting}
            size="large"
            variant="primary"
            title="Export to Excel"
          />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Item</th>
              <th
                style={{
                  padding: '0.75rem',
                  textAlign: 'right',
                  fontWeight: '600',
                  minWidth: '180px',
                }}
              >
                Revenue
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>Qty</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                Avg Sale
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                Min Sale
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                Max Sale
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                Avg Price
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                Min Price
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                Max Price
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => {
              const qtyChangeColor = item.qtyChange >= 0 ? '#28a745' : '#dc3545';
              const avgSaleChangeColor = item.avgSaleChange >= 0 ? '#28a745' : '#dc3545';
              const minSaleChangeColor = item.minSaleChange >= 0 ? '#28a745' : '#dc3545';
              const maxSaleChangeColor = item.maxSaleChange >= 0 ? '#28a745' : '#dc3545';
              const avgPriceChangeColor = item.avgPriceChange >= 0 ? '#28a745' : '#dc3545';
              const minPriceChangeColor = item.minPriceChange >= 0 ? '#28a745' : '#dc3545';
              const maxPriceChangeColor = item.maxPriceChange >= 0 ? '#28a745' : '#dc3545';
              const isClickable = item.itemId !== undefined;

              return (
                <tr
                  key={item.itemId !== undefined ? `${item.itemId}-${index}` : `item-${index}`}
                  onClick={() => isClickable && handleItemClick(item, segmentType)}
                  style={{
                    borderBottom: '1px solid #dee2e6',
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <td style={{ padding: '0.75rem' }}>
                    <div>
                      {item.item}
                      {item.itemId && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            color: '#6c757d',
                            fontWeight: '400',
                          }}
                        >
                          ({item.itemId})
                        </span>
                      )}
                      {item.productCode && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            color: '#6c757d',
                            fontWeight: '400',
                          }}
                        >
                          [{item.productCode}]
                        </span>
                      )}
                    </div>
                    {isClickable && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#007bff' }}>
                        ▶
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    {(() => {
                      const projections = calculateProjection(item);
                      if (filters.compareMode) {
                        const projectionChange =
                          projections.prior !== null && projections.current !== null
                            ? projections.current - projections.prior
                            : null;
                        const projectionChangePercent =
                          projections.prior !== null &&
                          projections.prior !== 0 &&
                          projectionChange !== null
                            ? (projectionChange / Math.abs(projections.prior)) * 100
                            : null;
                        const projectionChangeColor =
                          projectionChange !== null && projectionChange >= 0
                            ? '#28a745'
                            : '#dc3545';

                        return (
                          <>
                            <div style={{ fontSize: '0.75rem' }}>
                              Current: {formatCurrency(item.currentRevenue)}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                              Prior: {formatCurrency(item.priorRevenue)}
                            </div>
                            {projections.current !== null && (
                              <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                Project: {formatCurrency(projections.current)}
                              </div>
                            )}
                            {projectionChange !== null && projectionChangePercent !== null && (
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: projectionChangeColor,
                                  fontWeight: '500',
                                  marginTop: '0.25rem',
                                }}
                              >
                                {projectionChange >= 0 ? '↑' : '↓'}{' '}
                                {formatCurrency(Math.abs(projectionChange))} (
                                {formatPercentage(projectionChangePercent)})
                              </div>
                            )}
                          </>
                        );
                      } else {
                        return <div>{formatCurrency(item.currentRevenue)}</div>;
                      }
                    })()}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatNumber(item.currentQty)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatNumber(item.priorQty)}
                        </div>
                        <div
                          style={{ fontSize: '0.75rem', color: qtyChangeColor, fontWeight: '500' }}
                        >
                          {item.qtyChange >= 0 ? '↑' : '↓'} {formatNumber(Math.abs(item.qtyChange))}{' '}
                          ({formatPercentage(item.qtyChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatCurrency(item.currentAvgSale)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatCurrency(item.priorAvgSale)}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: avgSaleChangeColor,
                            fontWeight: '500',
                          }}
                        >
                          {item.avgSaleChange >= 0 ? '↑' : '↓'}{' '}
                          {formatCurrency(Math.abs(item.avgSaleChange))} (
                          {formatPercentage(item.avgSaleChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatCurrency(item.currentMinSale)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatCurrency(item.priorMinSale)}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: minSaleChangeColor,
                            fontWeight: '500',
                          }}
                        >
                          {item.minSaleChange >= 0 ? '↑' : '↓'}{' '}
                          {formatCurrency(Math.abs(item.minSaleChange))} (
                          {formatPercentage(item.minSaleChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatCurrency(item.currentMaxSale)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatCurrency(item.priorMaxSale)}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: maxSaleChangeColor,
                            fontWeight: '500',
                          }}
                        >
                          {item.maxSaleChange >= 0 ? '↑' : '↓'}{' '}
                          {formatCurrency(Math.abs(item.maxSaleChange))} (
                          {formatPercentage(item.maxSaleChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatCurrency(item.currentAvgPrice)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatCurrency(item.priorAvgPrice)}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: avgPriceChangeColor,
                            fontWeight: '500',
                          }}
                        >
                          {item.avgPriceChange >= 0 ? '↑' : '↓'}{' '}
                          {formatCurrency(Math.abs(item.avgPriceChange))} (
                          {formatPercentage(item.avgPriceChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatCurrency(item.currentMinPrice)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatCurrency(item.priorMinPrice)}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: minPriceChangeColor,
                            fontWeight: '500',
                          }}
                        >
                          {item.minPriceChange >= 0 ? '↑' : '↓'}{' '}
                          {formatCurrency(Math.abs(item.minPriceChange))} (
                          {formatPercentage(item.minPriceChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    <div>{formatCurrency(item.currentMaxPrice)}</div>
                    {filters.compareMode && (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Prior: {formatCurrency(item.priorMaxPrice)}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: maxPriceChangeColor,
                            fontWeight: '500',
                          }}
                        >
                          {item.maxPriceChange >= 0 ? '↑' : '↓'}{' '}
                          {formatCurrency(Math.abs(item.maxPriceChange))} (
                          {formatPercentage(item.maxPriceChangePercent)})
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid #dee2e6',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
            Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of{' '}
            {sortedData.length} items
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
                color: currentPage === 1 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
                color: currentPage === totalPages ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedItemsTable;
